import {
  federalWasteGeneratorStatusToLabel,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste";
import {
  CalculatedGeneratorStatusSource,
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
} from "generated-graphql/graphql";

import { sourcesDisagree } from "../wasteStreamDiscrepancyIndicator";

/** One source's answer for a month, as the summary query returns it. */
export type CalculatedStatusBySource = {
  source: CalculatedGeneratorStatusSource;
  federalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  stateStatus?: StateRegulatoryStatus | null;
};

/** What each source is called in the tooltip: plain names a user recognizes, not stored strings. */
const SOURCE_LABEL: Record<CalculatedGeneratorStatusSource, string> = {
  [CalculatedGeneratorStatusSource.EManifest]: "e-Manifest",
  [CalculatedGeneratorStatusSource.Vendor]: "Vendor",
  [CalculatedGeneratorStatusSource.ReviewedDocument]: "Reviewed documents",
  [CalculatedGeneratorStatusSource.ContainerData]: "Container tracking",
};

/** A source's value for one axis, labeled for the tooltip. */
export type SourceStatusLine = {
  source: CalculatedGeneratorStatusSource;
  label: string;
  value: string;
};

/** A source's display name, or an em dash for a month no source answered. */
export function sourceLabel(source: CalculatedGeneratorStatusSource | null | undefined): string {
  return source ? SOURCE_LABEL[source] : "—";
}

/**
 * Whether this line is the source the status was taken from. The server picks it,
 * so the client just compares rather than ranking again.
 */
export function isUsedSourceLine(
  line: SourceStatusLine,
  effectiveSource: CalculatedGeneratorStatusSource | null | undefined
): boolean {
  return Boolean(effectiveSource) && line.source === effectiveSource;
}

/**
 * The per-source lines for one axis, skipping sources with no status that month:
 * no status means no waste to rate, not a zero, so listing it would invent a disagreement.
 */
export function sourceStatusLines(
  sources: readonly CalculatedStatusBySource[],
  axis: "federal" | "state"
): SourceStatusLine[] {
  const lines: SourceStatusLine[] = [];
  for (const entry of sources) {
    // Each axis is read on its own branch so the status keeps its own enum
    // type through the label lookup.
    const value =
      axis === "federal"
        ? entry.federalStatus && federalWasteGeneratorStatusToLabel(entry.federalStatus)
        : entry.stateStatus && stateWasteGeneratorStatusToLabel(entry.stateStatus);
    if (!value) continue;
    lines.push({ source: entry.source, label: SOURCE_LABEL[entry.source], value });
  }
  return lines;
}

/**
 * True when two or more sources answered this axis and disagree (the cell's (!)).
 * Compares rendered labels so statuses that read the same never show as a conflict.
 */
export function calculatedSourcesDisagree(
  sources: readonly CalculatedStatusBySource[],
  axis: "federal" | "state"
): boolean {
  return sourcesDisagree(sourceStatusLines(sources, axis).map((line) => line.value));
}
